import { useState } from 'react';
import Layout from '../../../components/feature/Layout';

interface Opportunity {
  id: string;
  name: string;
  client: string;
  service: string;
  value: string;
  probability: number;
  stage: 'Prospecto' | 'Cotizado' | 'Negociación' | 'Ganado' | 'Perdido';
  executive: string;
  closingDate: string;
}

export default function OpportunitiesPage() {
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedOpportunity, setSelectedOpportunity] = useState<Opportunity | null>(null);
  const [filterStage, setFilterStage] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');

  const opportunities: Opportunity[] = [
    { id: 'OPP-001', name: 'Importación Maquinaria Industrial', client: 'Importadora del Pacífico S.A.', service: 'Importación Marítima', value: '$125,000', probability: 80, stage: 'Negociación', executive: 'Juan Delgado', closingDate: '2024-02-15' },
    { id: 'OPP-002', name: 'Exportación Textiles', client: 'Textiles Modernos S.A.', service: 'Exportación Aérea', value: '$85,000', probability: 60, stage: 'Cotizado', executive: 'María López', closingDate: '2024-02-20' },
    { id: 'OPP-003', name: 'Importación Alimentos', client: 'Alimentos Premium', service: 'Importación Terrestre', value: '$45,000', probability: 90, stage: 'Negociación', executive: 'Carlos Méndez', closingDate: '2024-02-10' },
    { id: 'OPP-004', name: 'Exportación Café', client: 'Exportadora Central', service: 'Exportación Marítima', value: '$200,000', probability: 40, stage: 'Prospecto', executive: 'Ana Rodríguez', closingDate: '2024-03-01' },
    { id: 'OPP-005', name: 'Importación Electrónicos', client: 'Global Trade Solutions', service: 'Importación Aérea', value: '$150,000', probability: 70, stage: 'Cotizado', executive: 'Juan Delgado', closingDate: '2024-02-25' },
    { id: 'OPP-006', name: 'Exportación Frutas', client: 'Comercial Atlántico', service: 'Exportación Marítima', value: '$95,000', probability: 100, stage: 'Ganado', executive: 'María López', closingDate: '2024-01-30' },
    { id: 'OPP-007', name: 'Importación Repuestos', client: 'Importadora del Pacífico S.A.', service: 'Importación Terrestre', value: '$35,000', probability: 0, stage: 'Perdido', executive: 'Carlos Méndez', closingDate: '2024-01-25' },
    { id: 'OPP-008', name: 'Exportación Químicos', client: 'Textiles Modernos S.A.', service: 'Exportación Aérea', value: '$110,000', probability: 85, stage: 'Negociación', executive: 'Ana Rodríguez', closingDate: '2024-02-18' },
  ];

  const getStageColor = (stage: string) => {
    switch (stage) {
      case 'Prospecto': return 'bg-gray-100 text-gray-700';
      case 'Cotizado': return 'bg-blue-100 text-blue-700';
      case 'Negociación': return 'bg-yellow-100 text-yellow-700';
      case 'Ganado': return 'bg-green-100 text-green-700';
      case 'Perdido': return 'bg-red-100 text-red-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  const filteredOpportunities = opportunities.filter(opp => {
    const matchesSearch = opp.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         opp.client.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStage = filterStage === 'all' || opp.stage === filterStage;
    return matchesSearch && matchesStage;
  });

  const totalValue = opportunities.reduce((sum, opp) => sum + parseFloat(opp.value.replace(/[$,]/g, '')), 0);
  const wonValue = opportunities.filter(o => o.stage === 'Ganado').reduce((sum, opp) => sum + parseFloat(opp.value.replace(/[$,]/g, '')), 0);
  const activeOpportunities = opportunities.filter(o => !['Ganado', 'Perdido'].includes(o.stage)).length;

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-[#0A2A43]">Oportunidades</h1>
            <p className="text-sm text-gray-500 mt-1">Gestión del pipeline comercial</p>
          </div>
          <button
            onClick={() => setShowCreateModal(true)}
            className="px-4 py-2.5 bg-gradient-to-r from-[#114B7A] to-[#2F80ED] text-white rounded-lg hover:shadow-lg transition-all flex items-center gap-2 whitespace-nowrap cursor-pointer"
          >
            <i className="ri-add-line text-lg"></i>
            Nueva Oportunidad
          </button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="bg-white rounded-lg p-5 shadow-[0_4px_16px_rgba(10,42,67,0.08)]">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Oportunidades Activas</p>
                <p className="text-2xl font-bold text-[#0A2A43] mt-1">{activeOpportunities}</p>
              </div>
              <div className="w-12 h-12 flex items-center justify-center rounded-lg bg-[#EAF2F8]">
                <i className="ri-line-chart-line text-2xl text-[#2F80ED]"></i>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-lg p-5 shadow-[0_4px_16px_rgba(10,42,67,0.08)]">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Valor Total Pipeline</p>
                <p className="text-2xl font-bold text-[#0A2A43] mt-1">${(totalValue / 1000).toFixed(0)}K</p>
              </div>
              <div className="w-12 h-12 flex items-center justify-center rounded-lg bg-[#EAF2F8]">
                <i className="ri-money-dollar-circle-line text-2xl text-[#10B981]"></i>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-lg p-5 shadow-[0_4px_16px_rgba(10,42,67,0.08)]">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Ganadas este mes</p>
                <p className="text-2xl font-bold text-[#0A2A43] mt-1">${(wonValue / 1000).toFixed(0)}K</p>
              </div>
              <div className="w-12 h-12 flex items-center justify-center rounded-lg bg-[#EAF2F8]">
                <i className="ri-trophy-line text-2xl text-[#EAB308]"></i>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-lg p-5 shadow-[0_4px_16px_rgba(10,42,67,0.08)]">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Tasa de Conversión</p>
                <p className="text-2xl font-bold text-[#0A2A43] mt-1">68%</p>
              </div>
              <div className="w-12 h-12 flex items-center justify-center rounded-lg bg-[#EAF2F8]">
                <i className="ri-percent-line text-2xl text-[#114B7A]"></i>
              </div>
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-lg p-4 shadow-[0_4px_16px_rgba(10,42,67,0.08)]">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <div className="relative">
                <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"></i>
                <input
                  type="text"
                  placeholder="Buscar por nombre o cliente..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2F80ED] focus:border-transparent text-sm"
                />
              </div>
            </div>
            <div>
              <select
                value={filterStage}
                onChange={(e) => setFilterStage(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2F80ED] focus:border-transparent text-sm cursor-pointer"
              >
                <option value="all">Todas las etapas</option>
                <option value="Prospecto">Prospecto</option>
                <option value="Cotizado">Cotizado</option>
                <option value="Negociación">Negociación</option>
                <option value="Ganado">Ganado</option>
                <option value="Perdido">Perdido</option>
              </select>
            </div>
          </div>
        </div>

        {/* Table */}
        <div className="bg-white rounded-lg shadow-[0_4px_16px_rgba(10,42,67,0.08)] overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-[#EAF2F8]">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-[#0A2A43] uppercase tracking-wider">Oportunidad</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-[#0A2A43] uppercase tracking-wider">Cliente</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-[#0A2A43] uppercase tracking-wider">Servicio</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-[#0A2A43] uppercase tracking-wider">Valor</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-[#0A2A43] uppercase tracking-wider">Probabilidad</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-[#0A2A43] uppercase tracking-wider">Etapa</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-[#0A2A43] uppercase tracking-wider">Ejecutivo</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-[#0A2A43] uppercase tracking-wider">Cierre Est.</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-[#0A2A43] uppercase tracking-wider">Acciones</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {filteredOpportunities.map((opp) => (
                  <tr key={opp.id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-6 py-4">
                      <div>
                        <p className="text-sm font-medium text-[#0A2A43]">{opp.name}</p>
                        <p className="text-xs text-gray-500">{opp.id}</p>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <p className="text-sm text-[#0A2A43]">{opp.client}</p>
                    </td>
                    <td className="px-6 py-4">
                      <p className="text-sm text-[#0A2A43]">{opp.service}</p>
                    </td>
                    <td className="px-6 py-4">
                      <p className="text-sm font-semibold text-[#0A2A43]">{opp.value}</p>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        <div className="flex-1 bg-gray-200 rounded-full h-2">
                          <div
                            className="bg-gradient-to-r from-[#114B7A] to-[#2F80ED] h-2 rounded-full"
                            style={{ width: `${opp.probability}%` }}
                          ></div>
                        </div>
                        <span className="text-sm font-medium text-[#0A2A43] w-10">{opp.probability}%</span>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <span className={`px-2.5 py-1 rounded-full text-xs font-medium whitespace-nowrap ${getStageColor(opp.stage)}`}>
                        {opp.stage}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <p className="text-sm text-[#0A2A43]">{opp.executive}</p>
                    </td>
                    <td className="px-6 py-4">
                      <p className="text-sm text-[#0A2A43]">{opp.closingDate}</p>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => {
                            setSelectedOpportunity(opp);
                            setShowDetailModal(true);
                          }}
                          className="w-8 h-8 flex items-center justify-center rounded-md hover:bg-[#EAF2F8] text-[#2F80ED] transition-colors cursor-pointer"
                          title="Ver detalle"
                        >
                          <i className="ri-eye-line text-lg"></i>
                        </button>
                        <button
                          className="w-8 h-8 flex items-center justify-center rounded-md hover:bg-[#EAF2F8] text-[#114B7A] transition-colors cursor-pointer"
                          title="Cambiar etapa"
                        >
                          <i className="ri-arrow-right-circle-line text-lg"></i>
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Create Modal */}
        {showCreateModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg w-full max-w-2xl max-h-[90vh] overflow-y-auto">
              <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
                <h2 className="text-xl font-bold text-[#0A2A43]">Nueva Oportunidad</h2>
                <button
                  onClick={() => setShowCreateModal(false)}
                  className="w-8 h-8 flex items-center justify-center rounded-md hover:bg-gray-100 transition-colors cursor-pointer"
                >
                  <i className="ri-close-line text-xl text-gray-500"></i>
                </button>
              </div>
              <div className="p-6 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-[#0A2A43] mb-1">Nombre de la Oportunidad *</label>
                  <input type="text" className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2F80ED] focus:border-transparent text-sm" />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-[#0A2A43] mb-1">Cliente *</label>
                    <select className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2F80ED] focus:border-transparent text-sm cursor-pointer">
                      <option>Seleccionar cliente</option>
                      <option>Importadora del Pacífico S.A.</option>
                      <option>Exportadora Central</option>
                      <option>Global Trade Solutions</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-[#0A2A43] mb-1">Tipo de Servicio *</label>
                    <select className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2F80ED] focus:border-transparent text-sm cursor-pointer">
                      <option>Seleccionar servicio</option>
                      <option>Importación Marítima</option>
                      <option>Importación Aérea</option>
                      <option>Importación Terrestre</option>
                      <option>Exportación Marítima</option>
                      <option>Exportación Aérea</option>
                    </select>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-[#0A2A43] mb-1">Valor Estimado *</label>
                    <input type="text" placeholder="$0.00" className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2F80ED] focus:border-transparent text-sm" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-[#0A2A43] mb-1">Probabilidad (%)</label>
                    <input type="number" min="0" max="100" defaultValue="50" className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2F80ED] focus:border-transparent text-sm" />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-[#0A2A43] mb-1">Etapa *</label>
                    <select className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2F80ED] focus:border-transparent text-sm cursor-pointer">
                      <option>Prospecto</option>
                      <option>Cotizado</option>
                      <option>Negociación</option>
                      <option>Ganado</option>
                      <option>Perdido</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-[#0A2A43] mb-1">Ejecutivo Asignado *</label>
                    <select className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2F80ED] focus:border-transparent text-sm cursor-pointer">
                      <option>Juan Delgado</option>
                      <option>María López</option>
                      <option>Carlos Méndez</option>
                      <option>Ana Rodríguez</option>
                    </select>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#0A2A43] mb-1">Fecha Estimada de Cierre</label>
                  <input type="date" className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2F80ED] focus:border-transparent text-sm" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#0A2A43] mb-1">Notas</label>
                  <textarea rows={3} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2F80ED] focus:border-transparent text-sm resize-none"></textarea>
                </div>
              </div>
              <div className="sticky bottom-0 bg-gray-50 px-6 py-4 flex items-center justify-end gap-3 border-t border-gray-200">
                <button
                  onClick={() => setShowCreateModal(false)}
                  className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 transition-colors whitespace-nowrap cursor-pointer"
                >
                  Cancelar
                </button>
                <button
                  onClick={() => setShowCreateModal(false)}
                  className="px-4 py-2 bg-gradient-to-r from-[#114B7A] to-[#2F80ED] text-white rounded-lg hover:shadow-lg transition-all whitespace-nowrap cursor-pointer"
                >
                  Crear Oportunidad
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Detail Modal */}
        {showDetailModal && selectedOpportunity && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg w-full max-w-3xl max-h-[90vh] overflow-y-auto">
              <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
                <h2 className="text-xl font-bold text-[#0A2A43]">Detalle de Oportunidad</h2>
                <button
                  onClick={() => setShowDetailModal(false)}
                  className="w-8 h-8 flex items-center justify-center rounded-md hover:bg-gray-100 transition-colors cursor-pointer"
                >
                  <i className="ri-close-line text-xl text-gray-500"></i>
                </button>
              </div>
              <div className="p-6 space-y-6">
                <div>
                  <h3 className="text-xl font-bold text-[#0A2A43]">{selectedOpportunity.name}</h3>
                  <p className="text-sm text-gray-500">{selectedOpportunity.id}</p>
                  <span className={`inline-block mt-2 px-2.5 py-1 rounded-full text-xs font-medium ${getStageColor(selectedOpportunity.stage)}`}>
                    {selectedOpportunity.stage}
                  </span>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-gray-500 mb-1">Cliente</p>
                    <p className="text-sm font-medium text-[#0A2A43]">{selectedOpportunity.client}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500 mb-1">Servicio</p>
                    <p className="text-sm font-medium text-[#0A2A43]">{selectedOpportunity.service}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500 mb-1">Valor Estimado</p>
                    <p className="text-sm font-medium text-[#0A2A43]">{selectedOpportunity.value}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500 mb-1">Probabilidad</p>
                    <p className="text-sm font-medium text-[#0A2A43]">{selectedOpportunity.probability}%</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500 mb-1">Ejecutivo Asignado</p>
                    <p className="text-sm font-medium text-[#0A2A43]">{selectedOpportunity.executive}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500 mb-1">Fecha de Cierre Estimada</p>
                    <p className="text-sm font-medium text-[#0A2A43]">{selectedOpportunity.closingDate}</p>
                  </div>
                </div>
                <div>
                  <h4 className="text-sm font-semibold text-[#0A2A43] mb-3">Historial de Actividades</h4>
                  <div className="space-y-3">
                    <div className="flex gap-3 p-3 bg-gray-50 rounded-lg">
                      <i className="ri-arrow-right-circle-line text-[#2F80ED] text-lg"></i>
                      <div className="flex-1">
                        <p className="text-sm font-medium text-[#0A2A43]">Cambio de etapa a Negociación</p>
                        <p className="text-xs text-gray-500">Hace 2 días por {selectedOpportunity.executive}</p>
                      </div>
                    </div>
                    <div className="flex gap-3 p-3 bg-gray-50 rounded-lg">
                      <i className="ri-file-text-line text-[#10B981] text-lg"></i>
                      <div className="flex-1">
                        <p className="text-sm font-medium text-[#0A2A43]">Cotización enviada</p>
                        <p className="text-xs text-gray-500">Hace 5 días por {selectedOpportunity.executive}</p>
                      </div>
                    </div>
                    <div className="flex gap-3 p-3 bg-gray-50 rounded-lg">
                      <i className="ri-phone-line text-[#EAB308] text-lg"></i>
                      <div className="flex-1">
                        <p className="text-sm font-medium text-[#0A2A43]">Llamada de seguimiento</p>
                        <p className="text-xs text-gray-500">Hace 1 semana por {selectedOpportunity.executive}</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="sticky bottom-0 bg-gray-50 px-6 py-4 flex items-center justify-end gap-3 border-t border-gray-200">
                <button
                  onClick={() => setShowDetailModal(false)}
                  className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 transition-colors whitespace-nowrap cursor-pointer"
                >
                  Cerrar
                </button>
                <button className="px-4 py-2 bg-gradient-to-r from-[#114B7A] to-[#2F80ED] text-white rounded-lg hover:shadow-lg transition-all whitespace-nowrap cursor-pointer">
                  Cambiar Etapa
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}